import { DateTime } from "luxon"
import { useMemo } from "react"
import { proxy, useSnapshot, ref } from "valtio"

import type { GetCurrentUserQuery } from "@/graphql/graphql"

export interface GetReservationPositionsArgs {
  tableId: string
  startTime: DateTime
  endTime: DateTime
}

export interface CreateReservationPrefilledValues {
  tableId: string
  startTime: string
  endTime: string
  reservationType?: "reservation" | "walk_in"
}

export type CalendarDimensions = {
  viewport: {
    width: number | null
    height: number | null
  }
  canvasContainer: {
    width: number | null
    height: number | null
  }
  canvas: {
    width: number | null
    height: number | null
  }
  timelineContainer: {
    width: number | null
    height: number | null
    top: number | null
  }
  timeline: {
    padding: number | null
    width: number | null
    columnWidth: number | null
    height: number | null
    top: number | null
    offset: number | null
  }
  headers: {
    width: number | null
    height: number | null
  }
  columnWidth: number | null
  tableGroupLabelHeight: number
  tableLabelHeight: number
  getReservationPosition: (args: GetReservationPositionsArgs) => {
    top: number
    left: number
    width: number
    height: number
  } | null
}

const store = proxy({
  currentUser: null as GetCurrentUserQuery["currentUser"] | null,
  selectedTableGroupIds: [] as string[],
  calendarDimensions: <CalendarDimensions>{
    viewport: {
      width: null,
      height: null,
    },
    canvasContainer: {
      width: null,
      height: null,
    },
    canvas: {
      width: null,
      height: null,
    },
    timelineContainer: {
      width: null,
      height: null,
      top: null,
    },
    timeline: {
      padding: null,
      width: null,
      columnWidth: null,
      height: null,
      top: null,
      offset: null,
    },
    headers: {
      width: null,
      height: null,
    },
    columnWidth: null,
    tableGroupLabelHeight: null,
    tableLabelHeight: null,
    getReservationPosition: () => null,
  },
  currentDate: ref(DateTime.local()) as DateTime | null,
  fullWidthMode: true,
  showCreateReservationDialog: false,
  showQuickCreateReservationDialog: false,
  createReservationPrefilledValues: null as CreateReservationPrefilledValues | null,
  quickCreateReservationPrefilledValues: null as CreateReservationPrefilledValues | null,

  setCurrentUser: (currentUser: GetCurrentUserQuery["currentUser"] | null) => {
    store.currentUser = currentUser
  },
  setSelectedTableGroupIds: (ids: string[]) => {
    store.selectedTableGroupIds = ids
  },
  setCalendarDimensions: (dimensions: CalendarDimensions) => {
    store.calendarDimensions = dimensions
  },
  setCurrentDate: (date: DateTime | Date) => {
    const dateObj = date instanceof DateTime ? date : DateTime.fromJSDate(date)
    store.currentDate = ref(dateObj)
    return dateObj
  },
  setFullWidthMode: (fullWidthMode: boolean) => {
    store.fullWidthMode = fullWidthMode
  },
  setShowCreateReservationDialog: (show: boolean, prefilledValues?: CreateReservationPrefilledValues) => {
    store.showCreateReservationDialog = show
    store.createReservationPrefilledValues = prefilledValues || null
  },
  setShowQuickCreateReservationDialog: (show: boolean, prefilledValues?: CreateReservationPrefilledValues) => {
    store.showQuickCreateReservationDialog = show
    store.quickCreateReservationPrefilledValues = prefilledValues || null
  },
})

export const useStore = () => useSnapshot(store)

const isObject = (value: any): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value)
const isObjectEmpty = (obj: any) => isObject(obj) && Object.keys(obj).length === 0
// prettier-ignore
const isEmpty = (value: any) => value === null || value === undefined || value === "" ||
  (Array.isArray(value) && value.length === 0) || isObjectEmpty(value)

export const useHydrate = (initialState: Partial<typeof store>) => {
  useMemo(() => {
    Object.entries(initialState).forEach(([key, value]) => {
      const currentValue = store[key]

      if (isEmpty(currentValue)) {
        store[key] = value
      }
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- JSON.stringify provides stable dependency check
  }, [JSON.stringify(initialState)])
}

export default store
